function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { useEffect, useMemo, useRef } from 'react';
import { useUpdate } from '@bydesign-ui/hooks';
import { guid } from '@bydesign-ui/utils';
import { usePrefixCls } from '@byte-design/config';
import { Down, Right, Up } from '../../../common/icons';
import { useMenu, useSubMenu } from '../../../hooks/menu';
import { RcClickTrigger, RcHoverTrigger } from '../../../hooks/popper';
import { useSecondChildExpandAnimation } from '../../../hooks/useSecondChildExpandAnimation';
import { RcPopover } from '../../../units/popover-wrapper';
export function SubMenuImpl(props) {
  var _usePrefixCls;

  var children = props.children,
      title = props.title,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      icon = props.icon;
  var menu = useMenu();
  var subMenu = useSubMenu();

  if (subMenu == null) {
    throw new Error('useSubMenu must be under RcSubMenu');
  }

  var opened = subMenu.getOpened();
  var active = subMenu.getActive();
  var level = subMenu.getLevel();
  var trigger = menu.getTrigger();

  var _menu$getExtraProps = menu.getExtraProps(),
      collapse = _menu$getExtraProps.collapse,
      theme = _menu$getExtraProps.theme,
      showLine = _menu$getExtraProps.showLine;

  var triggerId = useMemo(function () {
    return guid();
  }, []);
  var triggerIds = useMemo(function () {
    return [triggerId];
  }, []);
  var levelSpan = 24;
  var paddingLeft = '16px';
  var openIcon;

  if (!collapse) {
    paddingLeft = "".concat(level * levelSpan, "px");
    openIcon = opened ? React.createElement(Up, null) : React.createElement(Down, null);
  } else {
    openIcon = React.createElement(Right, null);
  }

  var forceUpdate = useUpdate();
  var contentVisibleRef = useRef(!collapse);
  var elementRef = useRef(null);
  var cls = usePrefixCls('submenu', (_usePrefixCls = {
    opened: opened
  }, _defineProperty(_usePrefixCls, theme, true), _defineProperty(_usePrefixCls, level, true), _defineProperty(_usePrefixCls, "line", showLine), _usePrefixCls));
  var submenuHeaderCls = usePrefixCls('submenu-header', {
    active: active,
    disabled: disabled
  });
  var itemLineTitleCls = usePrefixCls('menu-line-title');
  var menuLineIconCls = usePrefixCls('menu-line-icon');
  var submenuHeaderOpenIconCls = usePrefixCls('submenu-header-open-icon');
  var submenuContentCls = usePrefixCls('submenu-content');
  var submenuContentLineCls = usePrefixCls('submenu-content-line');
  var submenuContentHideCls = usePrefixCls('submenu-content-hide');
  var popoverCls = usePrefixCls('submenu-popover', _defineProperty({}, theme, true));
  var style = {
    paddingLeft: paddingLeft
  };

  var handleClick = function handleClick() {
    if (collapse) {
      return;
    }

    if (!disabled) {
      subMenu.setOpened(!opened, opened);
    }
  };

  var hidePopover = function hidePopover() {
    !disabled && subMenu.setOpened(false, true);
  };

  var showPopover = function showPopover() {
    !disabled && subMenu.setOpened(true);
  };

  var OpenIcon = React.createElement("span", {
    className: submenuHeaderOpenIconCls
  }, openIcon);

  var renderHeader = function renderHeader() {
    return React.createElement("div", {
      className: submenuHeaderCls,
      onClick: handleClick,
      style: style
    }, showLine && OpenIcon, icon && React.createElement("span", {
      className: menuLineIconCls
    }, icon), React.createElement("span", {
      className: itemLineTitleCls
    }, title), !showLine && OpenIcon);
  };

  var renderContent = function renderContent(show) {
    return React.createElement("div", {
      className: "".concat(submenuContentCls, " ").concat(show ? '' : submenuContentHideCls)
    }, children, showLine && React.createElement("div", {
      style: {
        left: "".concat(level * levelSpan + 8, "px")
      },
      className: submenuContentLineCls
    }));
  };

  var startExpandAnimation = useSecondChildExpandAnimation(elementRef, {
    disabled: collapse && !contentVisibleRef.current,
    expanded: opened
  });
  useEffect(startExpandAnimation, [opened, collapse]);
  useEffect(function () {
    contentVisibleRef.current = !collapse;

    if (collapse) {
      var timeoutId = setTimeout(function () {
        return forceUpdate();
      }, 400);
      return function () {
        return clearTimeout(timeoutId);
      };
    }
  }, [collapse]);
  var Trigger = trigger === 'click' ? RcClickTrigger : RcHoverTrigger;
  return React.createElement("div", {
    className: cls,
    ref: elementRef
  }, React.createElement(Trigger, {
    triggerId: triggerId,
    disabled: !collapse
  }, renderHeader()), React.createElement(RcPopover, {
    lazyInit: true,
    show: collapse && opened,
    getPopper: renderContent(true),
    singleGroupId: String(level),
    placement: "right-start",
    triggerIds: triggerIds,
    preventAroundOverflow: true,
    refType: "trigger",
    refId: triggerId,
    offset: {
      x: 4,
      y: 0
    },
    className: popoverCls,
    onHide: hidePopover,
    onShow: showPopover
  }), (!collapse || contentVisibleRef.current) && renderContent(!collapse && opened));
}